import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import AppHeader from '@/components/AppHeader';
import { Tournament } from '@/types';
import { getTournaments, deleteTournament, getTournamentStats, getTournamentData, getTeams, getParticipatingTeams } from '@/lib/supabase';
import { exportTournamentData } from '@/lib/export';
import { exportTournamentAsJSON, cloneTournament, importTournamentFromJSON } from '@/lib/tournamentTransfer';
import type { TournamentJSON } from '@/lib/tournamentTransfer';
import TournamentForm from '@/components/modals/TournamentForm';
import TournamentStatsModal from '@/components/modals/TournamentStatsModal';
import CloneTournamentModal from '@/components/modals/CloneTournamentModal';
import ImportTournamentModal from '@/components/modals/ImportTournamentModal';

interface TournamentWithStats extends Tournament {
  stats?: {
    total: number;
    completed: number;
    live: number;
    pending: number;
  };
}

interface TournamentManagementProps {
  onSignOut: () => void;
}

export default function TournamentManagement({ onSignOut }: TournamentManagementProps) {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [tournaments, setTournaments] = useState<TournamentWithStats[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTournament, setEditingTournament] = useState<Tournament | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [exportingJsonId, setExportingJsonId] = useState<string | null>(null);
  const [cloneTarget, setCloneTarget] = useState<Tournament | null>(null);
  const [cloningId, setCloningId] = useState<string | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [statsTournament, setStatsTournament] = useState<{ id: string; name: string } | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [importWarnings, setImportWarnings] = useState<string[]>([]);

  const loadTournaments = async () => {
    setLoading(true);
    try {
      const [data, teamsData] = await Promise.all([
        getTournaments(),
        getTeams(),
      ]);
      const tournamentsWithStats = await Promise.all(
        data.map(async (t) => {
          try {
            const stats = await getTournamentStats(t.id);
            return { ...t, stats };
          } catch {
            return { ...t, stats: { total: 0, completed: 0, live: 0, pending: 0 } };
          }
        })
      );
      setTournaments(tournamentsWithStats);
      setTeams(teamsData.map((t: any) => ({
        id: t.id,
        name: t.name,
        shortName: t.short_name,
        color: t.color,
        players: t.players,
      })));
    } catch (err) {
      console.error('Failed to load tournaments:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTournaments();
  }, []);

  const handleCreate = () => {
    setEditingTournament(null);
    setShowForm(true);
  };

  const handleEdit = (tournament: Tournament) => {
    setEditingTournament(tournament);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    const tournament = tournaments.find(t => t.id === id);
    const tournamentName = tournament?.name || 'this tournament';

    const hasMatches = tournament?.stats && (tournament.stats.total > 0 || tournament.stats.completed > 0);

    let confirmMessage = `Are you sure you want to delete "${tournamentName}"?`;
    if (hasMatches) {
      confirmMessage = `Tournament "${tournamentName}" has ${tournament?.stats?.total ?? 0} match(es). Matches will be unlinked but preserved.\n\nType "DELETE" to confirm deletion.`;
      const userInput = prompt(confirmMessage);
      if (userInput !== 'DELETE') {
        return;
      }
    } else {
      if (!confirm(confirmMessage)) {
        return;
      }
    }

    setDeletingId(id);
    setDeleteError(null);
    try {
      console.log('[DELETE TOURNAMENT] User confirmed delete for:', id);
      await deleteTournament(id);
      console.log('[DELETE TOURNAMENT] Successfully deleted tournament:', id);
      await loadTournaments();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete tournament';
      console.error('[DELETE TOURNAMENT] Error:', errorMessage);
      setDeleteError(errorMessage);
    } finally {
      setDeletingId(null);
    }
  };

  const handleFormSuccess = async () => {
    setShowForm(false);
    setEditingTournament(null);
    await loadTournaments();
  };

  const handleExportJSON = async (tournament: Tournament) => {
    setExportingJsonId(tournament.id);
    try {
      await exportTournamentAsJSON(tournament.id);
    } catch (err) {
      console.error('JSON export failed:', err);
    } finally {
      setExportingJsonId(null);
    }
  };

  const handleCloneConfirm = async (name: string, startDate: string, endDate: string) => {
    if (!cloneTarget) return;
    setCloningId(cloneTarget.id);
    try {
      const newId = await cloneTournament(cloneTarget.id, name, startDate, endDate);
      setCloneTarget(null);
      await loadTournaments();
      navigate(`/tournament/${newId}`);
    } finally {
      setCloningId(null);
    }
  };

  const handleImportConfirm = async (data: TournamentJSON, name: string, startDate: string, endDate: string) => {
    const result = await importTournamentFromJSON(data, name, startDate, endDate);
    setShowImportModal(false);
    if (result.unmatchedTeams.length > 0) {
      setImportWarnings(result.unmatchedTeams);
    }
    await loadTournaments();
    navigate(`/tournament/${result.tournamentId}`);
  };

  const handleViewTournament = (tournament: Tournament) => {
    navigate(`/tournament/${tournament.id}`);
  };

  const handleExportTournament = async (tournament: Tournament) => {
    setExportingId(tournament.id);
    try {
      const [data, participatingTeams] = await Promise.all([
        getTournamentData(tournament.id),
        getParticipatingTeams(tournament.id),
      ]);
      exportTournamentData(data, teams, tournament, participatingTeams);
    } catch (err) {
      console.error('Tournament export failed:', err);
    } finally {
      setExportingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <AppHeader
        onSignOut={onSignOut}
        title="Tournaments"
        rightExtra={
          /* Desktop only — on mobile the mobileSubRow handles these */
          !!isAdmin && (
            <div className="hidden sm:flex items-center gap-1">
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 active:scale-[0.98] transition-all font-bold text-[11px] leading-none"
                aria-label="Import tournament from JSON"
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                </svg>
                IMPORT
              </button>
              <button
                type="button"
                onClick={handleCreate}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-success/15 text-success hover:bg-success/25 active:scale-[0.98] transition-all font-bold text-[11px] leading-none"
                aria-label="New tournament"
              >
                <svg width="10" height="10" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 5v10M5 10h10" />
                </svg>
                NEW
              </button>
            </div>
          )
        }
        mobileSubRow={
          !!isAdmin ? (
            <div className="grid grid-cols-4 gap-2 w-full">
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                className="flex flex-col items-center gap-1 py-2 rounded-xl bg-primary/10 text-primary active:scale-[0.97] transition-all"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                </svg>
                <span className="text-[10px] font-bold leading-none">Import</span>
              </button>
              <button
                type="button"
                onClick={handleCreate}
                className="flex flex-col items-center gap-1 py-2 rounded-xl bg-success/15 text-success active:scale-[0.97] transition-all"
              >
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 5v10M5 10h10" />
                </svg>
                <span className="text-[10px] font-bold leading-none">New</span>
              </button>
              <button
                type="button"
                onClick={() => navigate('/admin')}
                className="flex flex-col items-center gap-1 py-2 rounded-xl bg-surface-raised text-text-secondary active:scale-[0.97] transition-all"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                <span className="text-[10px] font-bold leading-none">Admin</span>
              </button>
              <button
                type="button"
                onClick={onSignOut}
                className="flex flex-col items-center gap-1 py-2 rounded-xl bg-surface-raised text-text-muted active:scale-[0.97] transition-all hover:text-error"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16,17 21,12 16,7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                <span className="text-[10px] font-bold leading-none">Logout</span>
              </button>
            </div>
          ) : undefined
        }
      />

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4 safe-area-bottom">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <span className="w-8 h-8 border-2 border-text-muted border-t-transparent rounded-full animate-spin" />
          </div>
        ) : tournaments.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-full bg-surface-raised flex items-center justify-center mx-auto mb-4">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-text-muted">
                <path d="M16 8v16M8 16h16" />
              </svg>
            </div>
            <p className="text-text-secondary mb-4">No tournaments yet</p>
            {!!isAdmin && (
              <button onClick={handleCreate} className="btn-primary bg-success text-white">
                Create Tournament
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3 max-w-md mx-auto">
            {tournaments.map((tournament) => (
              <div
                key={tournament.id}
                className="card"
              >
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => handleViewTournament(tournament)}
                >
                  <div className="flex-1">
                    <h3 className="font-bold text-text-primary">{tournament.name}</h3>
                    <p className="text-sm text-text-secondary">
                      {new Date(tournament.startDate).toLocaleDateString()}
                      {tournament.endDate !== tournament.startDate && (
                        <> → {new Date(tournament.endDate).toLocaleDateString()}</>
                      )}
                      {tournament.venue && ` - ${tournament.venue}`}
                    </p>
                    {tournament.stats && (
                      <div className="flex gap-3 mt-2 text-xs">
                        <span className="text-text-muted">
                          {tournament.stats.total} matches
                        </span>
                        <span className="text-success">
                          {tournament.stats.completed} done
                        </span>
                        {tournament.stats.live > 0 && (
                          <span className="text-warning">
                            {tournament.stats.live} live
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-3 pt-3 border-t border-border grid grid-cols-3 gap-2">
                  <button
                    onClick={() => handleViewTournament(tournament)}
                    className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium bg-surface-raised hover:bg-accent/10 hover:text-accent transition-colors"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                    View
                  </button>
                  <button
                    onClick={() => setStatsTournament({ id: tournament.id, name: tournament.name })}
                    className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium bg-surface-raised hover:bg-accent/10 hover:text-accent transition-colors"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 20V10M12 20V4M6 20v-6" />
                    </svg>
                    Stats
                  </button>
                  <button
                    onClick={() => handleExportTournament(tournament)}
                    disabled={exportingId === tournament.id}
                    className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium bg-surface-raised hover:bg-success/10 hover:text-success transition-colors disabled:opacity-50"
                    title="Export as XLSX spreadsheet"
                  >
                    {exportingId === tournament.id ? (
                      <span className="w-3.5 h-3.5 border-2 border-success border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
                      </svg>
                    )}
                    XLSX
                  </button>
                </div>

                {/* JSON Export + Clone row */}
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <button
                    onClick={() => handleExportJSON(tournament)}
                    disabled={exportingJsonId === tournament.id}
                    className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium bg-surface-raised hover:bg-info/10 hover:text-info transition-colors disabled:opacity-50"
                    title="Export full data as JSON (for import/archive)"
                  >
                    {exportingJsonId === tournament.id ? (
                      <span className="w-3.5 h-3.5 border-2 border-info border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="12" y1="12" x2="12" y2="18" />
                        <line x1="9" y1="15" x2="15" y2="15" />
                      </svg>
                    )}
                    Export JSON
                  </button>
                  {!!isAdmin && (
                    <button
                      onClick={() => setCloneTarget(tournament)}
                      disabled={cloningId === tournament.id}
                      className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium bg-surface-raised hover:bg-warning/10 hover:text-warning transition-colors disabled:opacity-50"
                      title="Clone this tournament with all its matches and data"
                    >
                      {cloningId === tournament.id ? (
                        <span className="w-3.5 h-3.5 border-2 border-warning border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                      )}
                      Clone
                    </button>
                  )}
                </div>

                {/* Admin Actions */}
                {!!isAdmin && (
                  <div className="mt-2 flex gap-2">
                    <button
                      onClick={() => handleEdit(tournament)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium bg-surface-raised hover:text-text-primary transition-colors"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(tournament.id)}
                      disabled={deletingId === tournament.id}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium bg-surface-raised hover:text-error hover:bg-error/10 transition-colors disabled:opacity-50"
                    >
                      {deletingId === tournament.id ? (
                        <span className="w-3.5 h-3.5 border-2 border-error border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      )}
                      Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Delete Error Display */}
        {deleteError && (
          <div className="fixed bottom-20 left-4 right-4 max-w-md mx-auto bg-error/20 border border-error rounded-lg p-4 z-50">
            <div className="flex items-start gap-3">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-error flex-shrink-0 mt-0.5">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <div className="flex-1">
                <p className="text-error font-medium">Failed to delete tournament</p>
                <p className="text-text-secondary text-sm mt-1">{deleteError}</p>
              </div>
              <button
                onClick={() => setDeleteError(null)}
                className="text-text-muted hover:text-text-secondary"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tournament Form Modal */}
      {showForm && (
        <TournamentForm
          tournament={editingTournament}
          onSuccess={handleFormSuccess}
          onCancel={() => {
            setShowForm(false);
            setEditingTournament(null);
          }}
        />
      )}

      {/* Tournament Statistics Modal */}
      {statsTournament && (
        <TournamentStatsModal
          tournamentId={statsTournament.id}
          tournamentName={statsTournament.name}
          onClose={() => setStatsTournament(null)}
        />
      )}

      {/* Clone Modal */}
      {cloneTarget && (
        <CloneTournamentModal
          sourceName={cloneTarget.name}
          defaultStartDate={cloneTarget.startDate}
          defaultEndDate={cloneTarget.endDate}
          onConfirm={handleCloneConfirm}
          onCancel={() => setCloneTarget(null)}
        />
      )}

      {/* Import Modal */}
      {showImportModal && (
        <ImportTournamentModal
          onConfirm={handleImportConfirm}
          onCancel={() => setShowImportModal(false)}
        />
      )}

      {/* Import warnings */}
      {importWarnings.length > 0 && (
        <div className="fixed bottom-6 left-4 right-4 max-w-md mx-auto bg-warning/15 border border-warning/40 rounded-xl p-4 z-50 shadow-xl">
          <div className="flex items-start gap-3">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-warning flex-shrink-0 mt-0.5">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-warning">Some teams were not matched</p>
              <p className="text-xs text-text-secondary mt-0.5">
                Matches involving these teams were skipped:&nbsp;
                <span className="font-semibold">{importWarnings.join(', ')}</span>
              </p>
            </div>
            <button onClick={() => setImportWarnings([])} className="flex-shrink-0 text-text-muted hover:text-text-primary p-1">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6 6 18M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
